import React from 'react';
import { Chip, LinearProgress, Stack, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { FDataGrid } from '../grid';
import { BieMoney } from './BieValue';

type Props = { rows?: any[]; loading?: boolean };

const achievement = (row: any) => {
  const target = Number(row.targetvalue || 0);
  const actual = Number(row.actualvalue || 0);
  return target ? Math.round((actual / target) * 10000) / 100 : 0;
};

const status = (row: any) => {
  const pct = achievement(row);
  if (pct >= 100) return { label: 'Achieved', color: 'success' as const };
  if (pct >= 80) return { label: 'At Risk', color: 'warning' as const };
  return { label: 'Critical', color: 'error' as const };
};

const ObjectiveCell = (params: GridRenderCellParams) => (
  <Stack spacing={0.2}>
    <Typography variant="body2" sx={{ fontWeight: 700 }}>
      {params.row.objectivename || params.row.objectivecode || '-'}
    </Typography>
    <Typography variant="caption" color="text.secondary">{params.row.objectivecode || ''}</Typography>
  </Stack>
);

const AchievementCell = (params: GridRenderCellParams) => {
  const pct = achievement(params.row);
  return (
    <Stack spacing={0.5} sx={{ width: '100%', pt: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700 }}>{pct.toLocaleString('fr-FR')}%</Typography>
      <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, pct))} sx={{ height: 7, borderRadius: 5 }} />
    </Stack>
  );
};

const StatusCell = (params: GridRenderCellParams) => {
  const s = status(params.row);
  return <Chip size="small" label={s.label} color={s.color} />;
};

class ObjectiveGapGrid extends React.PureComponent<Props> {
  columns: GridColDef[] = [
    { field: 'objectivename', headerName: 'Objective', flex: 1, minWidth: 340, renderCell: ObjectiveCell },
    { field: 'targetvalue', headerName: 'Target', width: 160, align: 'right', headerAlign: 'right', renderCell: (p) => <BieMoney value={p.value} /> },
    { field: 'actualvalue', headerName: 'Actual', width: 160, align: 'right', headerAlign: 'right', renderCell: (p) => <BieMoney value={p.value} /> },
    { field: 'gapvalue', headerName: 'Gap', width: 170, align: 'right', headerAlign: 'right', renderCell: (p) => <BieMoney value={p.value} />, cellClassName: (p) => Number(p.value || 0) > 0 ? 'positive-gap' : Number(p.value || 0) < 0 ? 'negative-gap' : '' },
    { field: 'achievement', headerName: '% Achievement', width: 170, sortable: false, renderCell: AchievementCell },
    { field: 'status', headerName: 'Status', width: 130, sortable: false, renderCell: StatusCell },
  ];

  getRowId = (row: any) => row.objectiveid || row.objectivecode || row.id || row.rid || row.objectivename;

  render() {
    return (
      <FDataGrid
        title="Objective Gap Analysis"
        subtitle="Difference between planned objectives and simulated values."
        rows={this.props.rows || []}
        columns={this.columns}
        loading={this.props.loading}
        height={420}
        showQuickFilter
        toolbarSx={{ backgroundColor: '#eaf2fb' }}
        getRowId={this.getRowId}
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        sx={{
          '& .positive-gap': { bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 },
          '& .negative-gap': { bgcolor: '#ffebee', color: '#c62828', fontWeight: 700 },
        }}
      />
    );
  }
}

export default ObjectiveGapGrid;
